import { ArrowLeft, ChevronDown, Loader2, Search, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileLayout } from "../components/layout/MobileLayout.jsx";
import { Button } from "../components/ui/Button.jsx";
import { Card, CardContent } from "../components/ui/Card.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/Select.jsx";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/Sheet.jsx";
import { DatePicker } from "../components/ui/DatePicker.jsx";
import { useToast } from "../hooks/useToast.js";
import { fetchCompanyActivityLogs } from "../services/activityLogService.js";

const EVENT_TYPES = [
  "login",
  "lead_created",
  "lead_updated",
  "lead_deleted",
  "user_created",
];

const EVENT_COLORS = {
  login: "bg-blue-50 text-blue-600",
  lead_created: "bg-green-50 text-green-600",
  lead_updated: "bg-yellow-50 text-yellow-700",
  lead_deleted: "bg-red-50 text-red-600",
  user_created: "bg-purple-50 text-purple-600",
};

const EVENT_LABELS = {
  login: "Login",
  lead_created: "Lead Created",
  lead_updated: "Lead Updated",
  lead_deleted: "Lead Deleted",
  user_created: "User Created",
};

export default function AuditLogs() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);

  // Filters
  const [eventType, setEventType] = useState("");
  const [userId, setUserId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Detail sheet
  const [selectedLog, setSelectedLog] = useState(null);

  // Infinite scroll
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  const loadLogs = async (p = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const res = await fetchCompanyActivityLogs({
        page: p,
        limit: 20,
        event_type: eventType,
        user_id: userId,
        date_from: dateFrom,
        date_to: dateTo,
      });
      const newLogs = res.data || [];
      const pagination = res.pagination || {};

      if (append) {
        setLogs((prev) => [...prev, ...newLogs]);
      } else {
        setLogs(newLogs);
      }
      setHasMore(p < pagination.pages);
      setPage(p);
      if (res.team_members) setTeamMembers(res.team_members);
    } catch (err) {
      toast({ variant: "destructive", title: err.message });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    loadLogs(1);
  }, [eventType, userId, dateFrom, dateTo]);

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loading && !loadingMore) {
        loadLogs(page + 1, true);
      }
    },
    [hasMore, loading, loadingMore, page]
  );

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    if (sentinelRef.current) observerRef.current.observe(sentinelRef.current);
    return () => observerRef.current?.disconnect();
  }, [handleObserver]);

  const hasFilters = eventType || userId || dateFrom || dateTo;

  const clearFilters = () => {
    setEventType("");
    setUserId("");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <MobileLayout>
      <div className="px-4 pt-4 pb-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)} className="p-1">
              <ArrowLeft size={18} className="text-gray-600" />
            </button>
            <h1 className="text-base font-bold text-gray-900">Activity Logs</h1>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-[10px] gap-1"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Search size={12} />
            Filters
            {hasFilters && (
              <span className="w-1.5 h-1.5 rounded-full bg-[#00a8b4]" />
            )}
            <ChevronDown size={10} className={`transition-transform ${showFilters ? "rotate-180" : ""}`} />
          </Button>
        </div>

        {/* Filters panel */}
        {showFilters && (
          <Card className="border-gray-100">
            <CardContent className="p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Select value={userId || "all_users"} onValueChange={(v) => setUserId(v === "all_users" ? "" : v)}>
                  <SelectTrigger className="h-8 text-[11px]">
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_users">All Users</SelectItem>
                    {teamMembers.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={eventType || "all_events"} onValueChange={(v) => setEventType(v === "all_events" ? "" : v)}>
                  <SelectTrigger className="h-8 text-[11px]">
                    <SelectValue placeholder="All Events" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_events">All Events</SelectItem>
                    {EVENT_TYPES.map((e) => (
                      <SelectItem key={e} value={e}>
                        {EVENT_LABELS[e] || e}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <DatePicker
                  value={dateFrom}
                  onChange={setDateFrom}
                  placeholder="From"
                  className="h-8 text-[11px] w-full"
                />
                <DatePicker
                  value={dateTo}
                  onChange={setDateTo}
                  placeholder="To"
                  className="h-8 text-[11px] w-full"
                />
              </div>
              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-[10px] text-gray-500 w-full"
                  onClick={clearFilters}
                >
                  <X size={10} className="mr-1" />
                  Clear Filters
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Logs list */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xs text-gray-400">No activity logs found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map((log) => {
              let meta = null;
              try {
                meta = typeof log.metadata === "string" ? JSON.parse(log.metadata) : log.metadata;
              } catch {}

              const userName = log.user_name
                ? log.user_name
                : meta?.source === "facebook_ads"
                  ? "Facebook Ads"
                  : meta?.source === "webhook"
                    ? "Webhook"
                    : "System";

              const metaPreview = meta
                ? Object.entries(meta)
                    .filter(([k]) => k !== "source")
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(", ")
                : null;

              return (
                <Card
                  key={log.id}
                  className="border-gray-100 cursor-pointer active:bg-gray-50"
                  onClick={() => setSelectedLog({ ...log, _meta: meta, _userName: userName })}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-medium whitespace-nowrap ${
                              EVENT_COLORS[log.event_type] || "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {EVENT_LABELS[log.event_type] || log.event_type}
                          </span>
                          <span className="text-[11px] font-medium text-gray-800 truncate">
                            {userName}
                          </span>
                        </div>
                        {log.user_email && (
                          <p className="text-[10px] text-gray-400 truncate">{log.user_email}</p>
                        )}
                        {metaPreview && (
                          <p className="text-[10px] text-gray-400 mt-0.5 truncate">
                            {metaPreview}
                          </p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] text-gray-400">
                          {new Date(log.created_at).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                          })}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          {new Date(log.created_at).toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-4" />
        {loadingMore && (
          <div className="flex justify-center py-3">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          </div>
        )}
        {!hasMore && logs.length > 0 && (
          <p className="text-center text-[10px] text-gray-300 py-2">No more logs</p>
        )}
      </div>

      {/* Log detail sheet */}
      <Sheet open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[70dvh] overflow-y-auto">
          {selectedLog && (() => {
            const meta = selectedLog._meta;
            const userName = selectedLog._userName;
            return (
              <>
                <SheetHeader className="pb-3">
                  <SheetTitle className="text-sm flex items-center gap-2">
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                        EVENT_COLORS[selectedLog.event_type] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {EVENT_LABELS[selectedLog.event_type] || selectedLog.event_type}
                    </span>
                  </SheetTitle>
                  <SheetDescription className="sr-only">Activity log details</SheetDescription>
                </SheetHeader>

                <div className="space-y-3 text-xs">
                  <div className="grid grid-cols-[90px_1fr] gap-y-2.5">
                    <span className="text-gray-400">User</span>
                    <span className="text-gray-800 font-medium">{userName}</span>

                    {selectedLog.user_email && (
                      <>
                        <span className="text-gray-400">Email</span>
                        <span className="text-gray-600">{selectedLog.user_email}</span>
                      </>
                    )}

                    <span className="text-gray-400">Time</span>
                    <span className="text-gray-600">
                      {new Date(selectedLog.created_at).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>

                    {selectedLog.ip_address && (
                      <>
                        <span className="text-gray-400">IP Address</span>
                        <span className="text-gray-600 font-mono">{selectedLog.ip_address}</span>
                      </>
                    )}
                  </div>

                  {meta && Object.keys(meta).filter((k) => k !== "source").length > 0 && (
                    <div>
                      <p className="text-gray-400 mb-1.5">Details</p>
                      <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                        {Object.entries(meta)
                          .filter(([k]) => k !== "source")
                          .map(([k, v]) => (
                            <div key={k}>
                              <span className="text-gray-400">{k.replace(/_/g, " ")}:</span>{" "}
                              <span className="text-gray-700 break-all">{String(v)}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>
    </MobileLayout>
  );
}
